import { Injectable } from '@angular/core';
import { Http, RequestOptions, URLSearchParams, Headers, Response } from '@angular/http';
import { MatDialog } from '@angular/material';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { ToastrService } from 'ngx-toastr';
import { v1 as uuid } from 'uuid';
import * as JSZip from 'jszip';

import { AppinfoService } from './appinfo.service';
import { CommonRequestService } from './common-request.service';
import { ProjectService } from './project.service';
import { SocketService, SocketLog } from './socket.service';
import { UserService } from './user.service';

import { AppSettings } from './../app.settings';

import { TableColumn, TableRow } from './common-table.service';
import { FP } from './../../assets/TS/FileProcess';

export type UploadType = 'map' | 'arnold' | 'section';

export interface FeatureLayer {
	name: string;
	type: string; // 'fgdb', 'fgdbZip', 'shp', 'shpZip'
	fileName: string; // Name of the file which the layer belongs to.
	matchingColumns: any[];
	content: {
		fields: any[]; // TODO
		// epsg?: number;
		// epsgInput?: number; // At least one of epsgOrigin or epsgInput need to be provided
		[prop: string]: any
	}; // layer content
}
export interface EventLayer {
	name: string;
	nameWithExt: string; // Layer name with extension. e.g. 'abc.txt'
	zipName?: string;
	type: 'txt' | 'txtZip';
	headers: any[];
	matchingColumns: any[];
	previewData: string;
	delimiter?: string;
	unqualified?: boolean; // Whether it's qualified layer for uploading
	layerFileData?; // Data that belongs to that layer. might not be a complete file
}

export interface TextFileSeparator {
	name: string;
	value: string;
}
export interface FileType {
	name: string;
	value: string;
}
// Columns that need user to pick a file columns to match
export interface FixedColumn {
	name: string;
	modelName: string;
	required?: 'op' | 're' | 'na';
	defaultVal?: string;
}
export interface InterpretResultTxt {
	matchingColumns;
	headers;
	previewData;
	fileData?;
}

@Injectable()
export class CommonUploadService {

	// TODO: LBE/HPMS separate
	// Last upload page before data-summary
	// data-upload-arnold, data-upload-map, or data-upload-section
	public lastUploadPage: UploadType = null;

	// The number of rows shown when previewing a file.
	public previewRowNum = 10;
	constructor(
		private appinfoService: AppinfoService,
		private requestService: CommonRequestService,
		private userService: UserService,
		private socketService: SocketService,
	) { }
	getSeparators(): TextFileSeparator[] {
		return [
			{
				name: 'Tab',
				value: '\t'
			},
			{
				name: 'Comma',
				value: ','
			},
			{
				name: 'Vertical Bar',
				value: '|'
			}
		];
	}

	getEventFileTypes(): FileType[] {
		return [
			{
				name: 'CSV',
				value: '.txt,.csv,.zip' // for use of [attr.accept]
			}
		];
	}
	getEventFileColumns(): FixedColumn[] {
		return this.buildEventFileColumns();
	}




	getFeatureFileTypes(): FileType[] {
		return [
			{
				name: 'shapefile',
				value: '.shp,.dbf,.shx,.prj'
			}, {
				name: 'fgdb',
				value: '.zip'
			}
		];
	}
	getFeatureFileColumns(): FixedColumn[] {
		return this.buildFeatureFileColumns();
	}

	covertTextPreviewData(data: any[], separatorVal: string): {columns: TableColumn[]; rows: TableRow[]; } {
		const columns: TableColumn[] = [];
		const rows: TableRow[] = [];
		if (data && data.length) {
			const columnNames = data[0].split(separatorVal);
			columnNames.forEach((name) => {
				columns.push({
					name: name,
					prop: name,
					info: {
						fixedCellClass: ''
					}
				});
			});
			for (let i = 1; i < data.length; i++) {
				const rowData = data[i].split(separatorVal);
				const row = {};
				rowData.forEach((item, idx) => {
					row[columnNames[idx]] = item;
				});
				rows.push(row);
			}
		}
		return {
			columns: columns,
			rows: rows
		};
	}

	transformLoadedFiles(files: File[] | FileList, successCallback?: (data: FP.GroupedFiles) => any, errorCallback?) {
		FP.FileUtility.groupFiles(files, null, (rsp) => {
			FP.FileUtility.processFilesByType(rsp, (rsp2) => {
				if (successCallback) {
					successCallback(rsp2);
				}
			}, errorCallback);
		}, errorCallback);
	}
	// Read and process dropped files
	transformDroppedFiles(items: DataTransferItem[] | DataTransferItemList, successCallback?: (data: FP.GroupedFiles) => any, errorCallback?) {
		FP.FileUtility.getFilesFromDataTransferItems(items, (rsp) => {
			if (rsp) {
				FP.FileUtility.groupFilesMix(rsp, (rspGroupedFile) => {
					FP.FileUtility.processFilesByType(rspGroupedFile, (rsp2) => {
						if (successCallback) {
							successCallback(rsp2);
						}
					});
				}, errorCallback);
			}
		}, errorCallback);
	}
	addRawFiles(addedFiles: FP.GroupedFiles, targetFiles: FP.GroupedFiles) {
		const merged = FP.GeneralUtility.mergeObjectsLevel1([targetFiles, addedFiles]);
		targetFiles = merged;
	}
	// Remove event raw file layers
	removeEventRawFilesByLayer(layer: EventLayer, rawFiles: FP.GroupedFiles) {
		if (!layer) {
			console.log('Remove layer failed! layer: ' + layer);
			return;
		}
		switch (layer.type) {
			// Delete txt layer directly
			case 'txt':
				delete rawFiles[layer.type][layer.nameWithExt];
				break;
			// Delete txt zip
			case 'txtZip':
				delete rawFiles[layer.type][layer.zipName].layers[layer.nameWithExt];
				if (!Object.keys(rawFiles[layer.type][layer.zipName].layers).length) {
					delete rawFiles[layer.type][layer.zipName];
				}
		}
	}

	getFeatureLayerMatchingColumns(featureType, fixedColumns, layerColumns) {
		return fixedColumns.map((column) => {
			const colInfo =  this.getColInfoByFeatureType(featureType, column.modelName);
			let matchedColumn = layerColumns.find((header) => {
				return header === colInfo.defaultVal;
			});
			if (!matchedColumn) { matchedColumn = ''; }
			// Duplicate an featureFileColumns
			return Object.assign({
				matchedColumn: matchedColumn,
				required: colInfo.state,
				defaultVal: colInfo.defaultVal
			}, column);
		});
	}
	private getColInfoByFeatureType(featureType, col) {
			// default 'reuired' is optional
			let required = 'op';
			let defaultVal = null;
			let colOptions;
			for (const prop of Object.keys(this.appinfoService.layerLoadMap)) {
				const item = this.appinfoService.layerLoadMap[prop];
				if (featureType) {
					if (featureType === 'PolyLineM') {
						if (item.layerCate === 'LRS Features') {
							colOptions = item.cols;
							break;
						}
					} else if (featureType === 'PointM' || featureType === 'PolyLine') {
						const featureTypeAlias = featureType === 'PointM' ? 'GPM' : 'GL';
						if (item.layerType === featureTypeAlias) {
							colOptions = item.cols;
							break;
						}
					} else {
						if (item.layerType === 'G') {
							colOptions = item.cols;
							break;
						}
					}
				}
			}
			if (colOptions) {
				switch (col) {
					case 'featureIdCol':
						required = colOptions.rec_id.state;
						defaultVal = colOptions.rec_id.default;
						break;
					case 'routeIdCol':
						required = colOptions.route_id.state;
						defaultVal = colOptions.route_id.default;
				}
			}
			return {
				state: required,
				defaultVal: defaultVal
			};
	}

	// Extract info from text file's binary string
	interpretTxtFile(binaryString: string,
		separator: string,
		previewRowNum?,
		fixedColumns?: FixedColumn[],
		options?: {fileData?: boolean}
	): InterpretResultTxt {
		options = options || {};
		let headers = [];
		let previewData = [];
		let matchedColumns;
		const result: InterpretResultTxt = {
			matchingColumns: null,
			headers: null,
			previewData: null
		};
		if (binaryString) {
			const data = binaryString.split('\n');
			// Get headers from the file
			if (data[0] && separator) {
				headers = data[0].split(separator);
			}
			// Set default previewRowNum
			if (previewRowNum && previewRowNum !== 0) {
				previewRowNum = 10;
			}
			// Store the first XX rows for previewing
			previewData = this.getPreviewData(data);
			//
			if (options.fileData) {
				result.fileData = data;
			}
		}
		if (fixedColumns) {
			matchedColumns = fixedColumns.map((column) => {
				let matchedColumn = '';
				if (headers.indexOf(column.defaultVal) !== -1) {
					matchedColumn = column.defaultVal;
				}
				// Duplicate an fixedColumn
				return Object.assign({
					matchedColumn: matchedColumn
				}, column);
			});
		}

		result.matchingColumns = matchedColumns;
		result.headers = headers;
		result.previewData = previewData;
		return result;
	}
	// Get previewData from fileData
	getPreviewData(fileData) {
		if (fileData.length > this.previewRowNum) {
			return fileData.slice(0, this.previewRowNum + 1);
		} else {
			return fileData;
		}
	}


	uploadFiles(
		type: UploadType,
		userSubId: number,
		projectId: number,
		layersBS: BehaviorSubject<EventLayer[] | FeatureLayer[]>,
		rawFiles,
		uploadingLogsBS: BehaviorSubject<{[processId: string]: SocketLog}>,
		arnoldLrsSpec?,
		delimiter?
	) {
		let viewDataState;
		let url = AppSettings.serviceRootUrl;
		const formData = new FormData();
		//
		// await this.separateEventLayersRawFiles();
		//
		const fileListJson = this.getFileListJson(type, projectId, layersBS.getValue(), rawFiles, arnoldLrsSpec, delimiter);
		//
		switch (this.lastUploadPage) {
			case 'map':
				// Append feature files
				for (const fileType of Object.keys(rawFiles)) {
					const files = rawFiles[fileType];
					for (const file of Object.keys(files)) {
						formData.append('uploads', files[file].zip);
					}
				}
				// Clear data
				this.initFeatureFilesRaw();
				layersBS.next([]);
				url += 'lbo/spatialFile';
				viewDataState = 'discovermaps';
				break;
			case 'arnold':
				// Append arnold files
				for (const file of Object.keys(rawFiles)) {
					for (const subfile of Object.keys(rawFiles[file])) {
						formData.append('uploads', rawFiles[file][subfile].zip);
					}
				}
				// Clear data
				this.initArnoldFilesRaw();
				layersBS.next([]);
				url += 'lbo/spatialFile';
				viewDataState = 'discovermaps';
				break;
			case 'section':
				// Append event files
				for (const fileType of Object.keys(rawFiles)) {
					for (const file of Object.keys(rawFiles[fileType])) {
						formData.append('uploads', rawFiles[fileType][file].zip);
					}
				}
				// Clear data
				this.initEventFilesRaw();
				layersBS.next([]);
				url += 'lbo/eventFile';
				viewDataState = 'discoverevents';
				break;
			default:
				return;
		}
		formData.append('sessionId', this.appinfoService.getSessionUuid());
		formData.append('spec', JSON.stringify(fileListJson));
		formData.append('userSubscriptionId', userSubId.toString());
		formData.append('userId', this.userService.getUserInfo().userId.toString());
		formData.append('agent', this.userService.getUserInfo().customerId.toString());
		formData.append('projectId', projectId.toString());

		// Generate a temporary processId before the true processId is returned from the service call
		const tempProcessId = uuid();
		const info = {
			// The arnold / map / section uploading have differenct viewDataState
			// So pass the state here to let the view-data btn in socket popup know where to go
			viewDataState: viewDataState
		};
		this.socketService.onInitProcess.next({
			tempId: tempProcessId,
			processType: 'Upload',
			info: info
		});

		return this.requestService.requestPost(url, formData,
			(rsp) => {
				const processesById = uploadingLogsBS.getValue();
				this.socketService.onInitProcess.next({
					tempId: tempProcessId,
					processId: rsp.data.content,
					processType: 'Upload'
					// status: 'Uploading'
				});
				const process = this.socketService.registerProcessById(rsp.data.content, processesById);
				// Update process info
				process.info = info;
				// process.toastId = toast.toastId;
				uploadingLogsBS.next(processesById);
			}, (err) => {
				// debugger;
				// Trigger a fake socket message to stop socket
				this.socketService.messageSubject.next({
					processId: tempProcessId,
					processType: 'Upload',
					isFinal: true,
					isError: true,
					isCanceled: false,
					dataType: 'json',
					data: { errorMessage: '', errorDetail: ''},
					timeStamp: new Date().toLocaleString('en-US')
				});
			},
			false);
	}


	initEventFilesRaw() {
		return {
			txt: {},
			txtZip: {}
		};
	}
	initFeatureFilesRaw() {
		return {
			fgdb: {},
			fgdbZip: {},
			shp: {},
			shpZip: {}
		};
	}
	initArnoldFilesRaw() {
		return {
			fgdb: {},
			fgdbZip: {},
			shp: {},
			shpZip: {}
		};
	}

	private getFileListJson(type: 'map' | 'arnold' | 'section', projectId: number, layers, rawFiles, arnoldLrsSpec?, delimiter?) {
		const fileLayers = [];
		switch (type) {
			case 'map':
			case 'arnold':
				// Update rawFiles layers info
				layers.forEach((l) => {
					const rawFile = rawFiles[l.type][l.fileName];
					switch (l.type) {
						case 'shp':
							rawFile.layerName = l.fileName;
							// rawFile.category = l.category;
							rawFile.matchingColumns = l.matchingColumns;
							// rawFile.epsg = l.content.epsg || l.content.epsgInput;
							rawFile.featureType = FP.FileSpec.convertFeatureType(l.content.type);
							break;
						default:
							const layer = rawFile.layers[l.name];
							layer.layerName = l.name;
							// layer.category = l.category;
							layer.matchingColumns = l.matchingColumns;
							// layer.epsg = l.content.epsg || l.content.epsgInput;
							layer.featureType = FP.FileSpec.convertFeatureType(l.content.type);
					}
				});
				for (const fileType of Object.keys(rawFiles)) {
					const files = rawFiles[fileType];
					switch (fileType) {
						case 'txt':
						case 'txtZip':
							break;
						case 'shp':
							for (const f of Object.keys(files)) {
								const fileJson = this.getFeatureFileJson_MultiLayers([files[f]], f, 'shapefile');
								fileLayers.push(fileJson);
							}
							break;
						default:
							for (const f of Object.keys(files)) {
								const fileJson = this.getFeatureFileJson_MultiLayers(files[f].layers, f, fileType);
								fileLayers.push(fileJson);
							}
					}
				}

				// Add lrsInfo if it's arnold
				if (type === 'arnold' && fileLayers[0]) {
					fileLayers[0].layers[0].layer_spec.lrs = arnoldLrsSpec;
				}
				break;
			case 'section':
				layers.forEach((l) => {
					let rawFile;
					switch (l.type) {
						case 'txt':
							rawFile = rawFiles[l.type][l.nameWithExt];
							rawFile.name = l.name;
							rawFile.nameWithExt = l.nameWithExt;
							// Add layers to rawFile
							if (!rawFile.layers) {
								rawFile.layers = {};
							}
							rawFile.layers[l.name] = l;
							break;
						case 'txtZip':
							rawFile = rawFiles[l.type][l.zipName].layers[l.nameWithExt];
							rawFile.name = l.name;
							rawFile.nameWithExt = l.nameWithExt;
							// Add layers to rawFile
							if (!rawFile.layers) {
								rawFile.layers = {};
							}
							rawFile.layers[l.name] = l;
					}
					rawFile.delimiter = delimiter;
				});
				for (const fileType of Object.keys(rawFiles)) {
					switch (fileType) {
						case 'txt':
							for (const f of Object.keys(rawFiles[fileType])) {
								const txtLayers = {};
								txtLayers[f] = rawFiles[fileType][f];
								const fileJson = this.getEventFileJson_MultiLayers(txtLayers, f);
								fileLayers.push(fileJson);
							}
							break;
						case 'txtZip':
							for (const zip of Object.keys(rawFiles[fileType])) {
								const fileJson = this.getEventFileJson_MultiLayers(rawFiles[fileType][zip].layers, zip);
								fileLayers.push(fileJson);
							}
					}
				}
		}
		return {
			prj_id: projectId,
			files: fileLayers
		};
	}

	// Get fileJson of feature files including fgdb, fgdbZip, shpZip
	getFeatureFileJson_MultiLayers(layers, name, type): FP.FilesJsonSpatial {
		const filesJson: FP.FilesJsonSpatial = FP.FileSpec.initFilesJsonSpatial();
		filesJson.name = name;
		switch (type) {
			case 'shapefile':
			case 'shpZip':
				filesJson.type = 'shapefile';
				break;
			case 'fgdb':
			case 'fgdbZip':
				filesJson.type = 'fgdb';
		}
		for (const layer of Object.keys(layers)) {
			const layerJson: FP.LayerJsonSpatial = this.getFeatureFileJson_SingleLayer(layers[layer], type);
			filesJson.layers.push(layerJson);
		}
		return filesJson;
	}

	private getFeatureFileJson_SingleLayer(file, type): FP.LayerJsonSpatial {
		const fileJson: FP.LayerJsonSpatial = FP.FileSpec.initLayerJson(false);
		fileJson.name = file.layerName;
		fileJson.sequence = null;
		// This is not in use currently since proj4 and geom_col is obtained at backend
		fileJson.layer_spec = {
			proj4: null,
			geom_col: null // 'geom'
		};
		fileJson.type = file.featureType;
		if (file.arnold || file.arnold === 0) {
			fileJson.arnold = file.arnold;
		}
		/*----------------- set lrs/attr columns ------------------*/
		const columnsInfo = this.getFeatureColumnsInfo(file.matchingColumns);
		const fields = (type === 'shapefile' || type === 'shpZip') ? file.info.fields : file.fields;
		const cols = this.setFeatureFileLrsAttrCols(columnsInfo, fields);
		fileJson.attr_cols = cols.attr;
		fileJson.lrs_cols = cols.lrs;
		return fileJson;
	}
	// Get fileJson of event files
	private getEventFileJson_MultiLayers(subfiles, name): FP.FilesJsonCsv {
		const layersJson = [];
		for (const sub of Object.keys(subfiles)) {
			for (const layerName of Object.keys(subfiles[sub].layers)) {
				const layerJson = this.getEventFileJson_SingleLayer(subfiles[sub].layers[layerName], subfiles[sub].delimiter);
				layersJson.push(layerJson);
			}
		}
		const filesJson: FP.FilesJsonCsv = FP.FileSpec.initFilesJsonCsv(name, layersJson);
		return filesJson;
	}
	private getEventFileJson_SingleLayer(layer: EventLayer, delimiter: string): FP.LayerJsonCsv {
		/*----------------------- set type -----------------------*/
		const columnsInfo = this.getEventColumnsInfo(layer.matchingColumns);
		const type = this.setEventFileType(columnsInfo);
		/*----------------- set lrs/attr columns ------------------*/
		const attrColumns = [];
		const lrsColumns = [
			{
				name: columnsInfo.eventId ? columnsInfo.eventId : null,
				lrs_type: 'rec_id'
			}, {
				name: columnsInfo.routeId ? columnsInfo.routeId : null,
				lrs_type: 'route_id'
			}, {
				name: columnsInfo.fromMeas ? columnsInfo.fromMeas : null,
				lrs_type: 'f_meas'
			}, {
				name: columnsInfo.toMeas ? columnsInfo.toMeas : null,
				lrs_type: 't_meas'
			}, {
				name: columnsInfo.fromDate ? columnsInfo.fromDate : null,
				lrs_type: 'b_date'
			}, {
				name: columnsInfo.toDate ? columnsInfo.toDate : null,
				lrs_type: 'e_date'
			}
		];
		const lrsColumnNames = [];
		for (const key of Object.keys(columnsInfo)) {
			lrsColumnNames.push(columnsInfo[key]);
		}
		for (let i = 0; i < layer.headers.length; i++) {
			if (lrsColumnNames.indexOf(layer.headers[i]) === -1) {
				attrColumns.push({
					name: layer.headers[i],
					data_type: 'string'
				});
			}
		}
		const layerJson: FP.LayerJsonCsv = FP.FileSpec.initLayerJsonCsv(layer.name, layer.nameWithExt, type, delimiter, lrsColumns, attrColumns);
		return layerJson;
	}
	// For event files, transform matchingColumns info to columnsInfo
	private getEventColumnsInfo(matchingColumns) {
		const columnsInfo: any = {
			fromMeas: '',
			toMeas: '',
			fromDate: '',
			toDate: ''
		};
		// transform matchingColumns to columnsInfo
		matchingColumns.forEach((info) => {
			switch (info.modelName) {
				case 'eventIdCol':
					columnsInfo.eventId = info.matchedColumn;
					break;
				case 'routeIdCol':
					columnsInfo.routeId = info.matchedColumn;
					break;
				case 'fromMeasCol':
					columnsInfo.fromMeas = info.matchedColumn;
					break;
				case 'toMeasCol':
					columnsInfo.toMeas = info.matchedColumn;
					break;
				case 'fromDateCol':
					columnsInfo.fromDate = info.matchedColumn;
					break;
				case 'toDateCol':
					columnsInfo.toDate = info.matchedColumn;
					break;
			}
		});
		return columnsInfo;
	}
	// For feature files, transform matchingColumns info to columnsInfo
	private getFeatureColumnsInfo(matchingColumns) {
		const columnsInfo: any = {
			featureId: '',
			routeId: ''
		};
		matchingColumns.forEach((info) => {
			switch (info.modelName) {
				case 'featureIdCol':
					columnsInfo.featureId = info.matchedColumn;
					break;
				case 'routeIdCol':
					columnsInfo.routeId = info.matchedColumn;
					break;
			}
		});
		return columnsInfo;
	}
	private setEventFileType(columnsInfo): string {
		let type = '';
		// fromDate or toDate doesn't exist
		if (!columnsInfo.fromDate || !columnsInfo.toDate) {
			if (columnsInfo.fromMeas === columnsInfo.toMeas) {
				type = 'EP';
			} else {
				type = 'EL';
			}
		}
		// same fromDate and toDate
		else if (columnsInfo.fromDate === columnsInfo.toDate) {
			if (columnsInfo.fromMeas === columnsInfo.toMeas) {
				type = 'EPI';
			} else {
				type = 'ELI';
			}
		}
		// different fromDate and toDate
		else {
			if (columnsInfo.fromMeas === columnsInfo.toMeas) {
				type = 'EPP';
			} else {
				type = 'ELP';
			}
		}
		return type;
	}
	// Set featuer file lrs/attr columns infomation
	private setFeatureFileLrsAttrCols(columnsInfo, fileHeaders) {
		const attrColumns = [];
		const lrsColumns = [
			{
				name: columnsInfo.featureId ? columnsInfo.featureId.name : null,
				lrs_type: 'rec_id'
			}, {
				name: columnsInfo.routeId ? columnsInfo.routeId.name : null,
				lrs_type: 'route_id'
			}
		];
		const lrsColumnNames = [];
		for (const key of Object.keys(columnsInfo)) {
			lrsColumnNames.push(columnsInfo[key]);
		}
		for (let i = 0; i < fileHeaders.length; i++) {
			if (lrsColumnNames.indexOf(fileHeaders[i].name) === -1) {
				attrColumns.push({
					name: fileHeaders[i].name,
					data_type: fileHeaders[i].LBType || 'string'
				});
			}
		}
		return {
			attr: attrColumns,
			lrs: lrsColumns
		};
	}


	private buildEventFileColumns(): FixedColumn[] {
		let colsMap;
		// Find event layer's columnMap from appinfo
		const eventLayerType = 'E';
		for (const cate of Object.keys(this.appinfoService.layerLoadMap)) {
			if (this.appinfoService.layerLoadMap[cate].layerType === eventLayerType) {
				colsMap = this.appinfoService.layerLoadMap[cate].cols;
				break;
			}
		}
		if (!colsMap) {
			console.log('No event layer load map !!');
			return;
		}
		return [
			{
				name: 'Event ID',
				modelName: 'eventIdCol',
				required: colsMap.rec_id.state,
				defaultVal: colsMap.rec_id.default
			}, {
				name: 'Route ID',
				modelName: 'routeIdCol',
				required: colsMap.route_id.state,
				defaultVal: colsMap.route_id.default
			}, {
				name: 'From Meas',
				modelName: 'fromMeasCol',
				required: colsMap.f_meas.state,
				defaultVal: colsMap.f_meas.default
			}, {
				name: 'To Meas',
				modelName: 'toMeasCol',
				required: colsMap.t_meas.state,
				defaultVal: colsMap.t_meas.default
			}, {
				name: 'From Date',
				modelName: 'fromDateCol',
				required: colsMap.b_date,
				defaultVal: colsMap.b_date.default
			}, {
				name: 'To Date',
				modelName: 'toDateCol',
				required: colsMap.e_date,
				defaultVal: colsMap.e_date.default
			}
		];
	}
	private buildFeatureFileColumns(): FixedColumn[] {
		// The order of items in lrsColumns matters
		// Arnold layer is hard coding using columns[1] as the column for selection
		return [
			{
				name: 'Feature ID',
				modelName: 'featureIdCol'
			}, {
				name: 'Route ID',
				modelName: 'routeIdCol'
			}
		];
	}

}



// WEBPACK FOOTER //
// ./src/app/common-services/common-upload.service.ts